
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    gtag: (command: string, ...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_MEASUREMENT_ID = 'G-E28SY7B1EN';

const GoogleAnalytics = () => {
  const location = useLocation();

  // Track page views on route changes
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location]);

  return null;
};

// Enhanced analytics event tracking functions
export const trackEvent = (eventName: string, parameters: Record<string, any> = {}) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, {
      event_category: parameters.category || 'engagement',
      event_label: parameters.label,
      value: parameters.value,
      ...parameters
    });
  }
};

// Conversion tracking functions with improved logging
export const trackBookingStart = () => {
  trackEvent('booking_started', {
    category: 'conversion',
    label: 'booking_form_opened'
  });
};

export const trackBookingSubmit = (serviceType?: string) => {
  trackEvent('booking_submitted', {
    category: 'conversion',
    label: 'booking_form_completed',
    custom_parameters: {
      service_type: serviceType || 'unknown'
    }
  });
};

export const trackEstimateRequest = (serviceType?: string) => {
  trackEvent('estimate_requested', {
    category: 'conversion', 
    label: 'estimate_form_submitted',
    custom_parameters: {
      service_type: serviceType || 'unknown'
    }
  });
};

export const trackPhoneCall = () => {
  trackEvent('phone_call_clicked', {
    category: 'conversion',
    label: 'phone_number_clicked'
  });
};

export const trackTextMessage = () => {
  trackEvent('text_message_clicked', {
    category: 'conversion',
    label: 'text_number_clicked'
  });
};

export const trackServiceView = (serviceName: string) => {
  trackEvent('service_viewed', {
    category: 'engagement',
    label: serviceName,
    custom_parameters: {
      service_name: serviceName
    }
  });
};

export const trackFormError = (formType: string, errorMessage: string) => {
  trackEvent('form_error', {
    category: 'error',
    label: formType,
    custom_parameters: {
      error_message: errorMessage
    }
  });
};

export default GoogleAnalytics;
